import type { ReactNode } from 'react';
import type { ChannelScopeResult } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, Icon } from '@mypoz/ui';

import { ChannelPicker } from '@/features/orders/components/ChannelPicker.js';
import type { ScopedChannels } from '@/features/orders/useScopedChannels.js';
import { buttonClass } from '@/lib/styles.js';

/**
 * Why the selected packages cannot be looked up, in the customer's terms. Shown on the packages
 * step, where the selection can be fixed, and again on the channel step, which it blocks.
 * Renders nothing for a selection that can be looked up, or for an empty one (the packages step
 * already asks for a package).
 */
export function ChannelScopeNotice({
  result,
  className,
}: {
  result: ChannelScopeResult;
  className?: string;
}) {
  const { t } = useTranslation();

  let message: string | undefined;
  if (result.kind === 'mixedWarehouses') message = t('orders.channelScope.mixedWarehouses');
  if (result.kind === 'mixedGoodsTypes') message = t('orders.channelScope.mixedGoodsTypes');
  if (result.kind === 'unknown') {
    message = t('orders.channelScope.unknown', {
      tracking: result.trackingNumbers.slice(0, 3).join(', '),
      count: result.trackingNumbers.length,
    });
  }
  if (message === undefined) return null;

  return (
    <p
      role="status"
      className={`border-l-2 border-warning pl-3 text-sm text-ink ${className ?? ''}`.trim()}
    >
      {message}
    </p>
  );
}

function StateCard({
  icon,
  message,
  action,
}: {
  icon: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <Card className="flex flex-col items-center gap-3 px-5 py-10 text-center">
      <Icon name={icon} size={28} className="text-muted" />
      <p className="text-sm text-ink">{message}</p>
      {action}
    </Card>
  );
}

/**
 * Step 2: the channel picker, for the channels the selected packages can actually use (MYP-87).
 *
 * Every state before the list gets its own words. Without them a list still loading, a failed
 * fetch and a selection that cannot be looked up all rendered as the same empty grid — and, since
 * the picker gained a search box, as "No channels match your search", which blames the customer.
 */
export function ChannelStep({
  scoped,
  selectedId,
  selectedName,
  cleared,
  onSelect,
}: {
  scoped: ScopedChannels;
  selectedId: string | undefined;
  /** A name for a selected channel the list does not include — an existing order's own. */
  selectedName?: string;
  /** Set when a channel chosen earlier was cleared because the new selection does not offer it. */
  cleared: boolean;
  onSelect: (channelId: string) => void;
}) {
  const { t } = useTranslation();
  const { result, status, channels, retry } = scoped;

  if (result.kind === 'noPackages') {
    return <StateCard icon="inventory_2" message={t('orders.channelScope.noPackages')} />;
  }

  if (result.kind !== 'ok') {
    return (
      <Card className="px-5 py-6">
        <ChannelScopeNotice result={result} />
      </Card>
    );
  }

  if (status === 'loading' || status === 'idle') {
    return <StateCard icon="hourglass_empty" message={t('orders.channelScope.loading')} />;
  }

  if (status === 'error') {
    return (
      <StateCard
        icon="cloud_off"
        message={t('orders.channelScope.error')}
        action={
          <button type="button" onClick={retry} className={buttonClass('secondary')}>
            <Icon name="refresh" size={18} />
            {t('orders.channelScope.retry')}
          </button>
        }
      />
    );
  }

  if (channels.length === 0) {
    return <StateCard icon="local_shipping" message={t('orders.channelScope.empty')} />;
  }

  return (
    <>
      {cleared ? (
        <p role="status" className="mb-4 border-l-2 border-warning pl-3 text-sm text-ink">
          {t('orders.channelScope.cleared')}
        </p>
      ) : null}
      <ChannelPicker
        channels={channels}
        selectedId={selectedId}
        selectedName={selectedName}
        onSelect={onSelect}
      />
    </>
  );
}
